// src/pages/OnusNoAutorizadas.js
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "./OnusNoAutorizadas.css";
import ModalAutorizarOnu from "../components/ModalAutorizarOnu";
import { authFetch } from "../utils/authFetch";
import {
  Boton, EncabezadoPagina, Iconos, Insignia, Tarjeta,
} from "../componentes/base";

export default function OnusNoAutorizadas() {
  const location = useLocation();
  // ✅ Si venimos del Dashboard, la lista ya consultada llega en location.state
  //    y NO se vuelve a consultar la OLT.
  const listaPrevia = Array.isArray(location.state?.onus)
    ? location.state.onus
    : null;

  const [onus, setOnus] = useState(listaPrevia || []);
  const [nuevoDomicilio, setNuevoDomicilio] = useState("");

  // Qué OLT se está consultando. Estaba fijo en 1 en cuatro lugares:
  // con un segundo nodo, autorizar allá habría escrito en el primero.
  const [olts, setOlts] = useState([]);
  const [oltId, setOltId] = useState(null);

  // Spinner carga autofind
  const [loadingAutofind, setLoadingAutofind] = useState(false);

  // Modal Autorizar ONU (nuevas)
  const [modalAutorizar, setModalAutorizar] = useState(false);
  const [onuSeleccionada, setOnuSeleccionada] = useState(null);

  // Modal Vision Pro Mudanza
  const [modalMudanza, setModalMudanza] = useState(false);
  const [onuMudanza, setOnuMudanza] = useState(null);
  const [loadingMudanza, setLoadingMudanza] = useState(false);

  // ✅ Datos del cliente para mudanza
  const [clienteMudanza, setClienteMudanza] = useState(null);
  const [loadingClienteMudanza, setLoadingClienteMudanza] = useState(false);

  // Modal iOS Confirm Eliminar
  const [modalDelete, setModalDelete] = useState(false);
  const [onuEliminar, setOnuEliminar] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  // ============================================================
  // HELPER TOAST
  // ============================================================
  function lanzarToast(tipo, mensaje) {
    if (window && window.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent("toast", {
          detail: { tipo, mensaje },
        })
      );
    }
  }

  // ============================================================
  // CARGAR AUTOFIND
  // ============================================================
  // La lista de nodos: si hay uno solo no hay nada que elegir.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch("/api/olt-config/");
        const data = await res.json();
        const lista = Array.isArray(data.olts) ? data.olts : [];
        setOlts(lista);
        if (lista.length > 0) setOltId(lista[0].id);
      } catch (e) {
        console.log("Error cargando OLTs:", e);
      }
    })();
  }, []);

  async function cargarAutofind(olt = oltId) {
    if (!olt) return;
    try {
      setLoadingAutofind(true);

      const res = await authFetch(`/api/olt/autofind/${olt}`);
      const data = await res.json();

      if (data.ok && Array.isArray(data.autofind)) {
        setOnus(data.autofind);
      } else {
        setOnus([]);
      }
    } catch (e) {
      console.log("Error cargando autofind:", e);
      setOnus([]);
    } finally {
      setLoadingAutofind(false);
    }
  }

  useEffect(() => {
    // Solo consulta la OLT si NO traemos la lista del Dashboard
    // (p. ej. cuando se entra directo por el menú).
    if (!listaPrevia) {
      cargarAutofind();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Quitar una ONU de la lista local (sin re-consultar la OLT)
  function quitarDeLista(serial) {
    setOnus((prev) => prev.filter((o) => o.sn !== serial));
  }

  // ============================================================
  // AUTORIZAR ONU (nueva)
  // ============================================================
  function abrirAutorizar(onu) {
    setOnuSeleccionada(onu);
    setModalAutorizar(true);
  }

  function cerrarAutorizar() {
    setModalAutorizar(false);
    setOnuSeleccionada(null);
  }

  // ============================================================
  // MUDANZA (ya registrada)
  // ============================================================
  async function abrirMudanza(onu) {
    setOnuMudanza(onu);
    setModalMudanza(true);

    // reset cliente + domicilio nuevo
    setClienteMudanza(null);
    setLoadingClienteMudanza(true);
    setNuevoDomicilio("");

    try {
      const serial = onu?.sn;
      if (!serial) {
        lanzarToast("error", "No se encontró el serial para consultar cliente.");
        setLoadingClienteMudanza(false);
        return;
      }

      const res = await authFetch(
        `/api/olt/cliente_por_serial/${encodeURIComponent(serial)}`
      );
      const data = await res.json();

      if (data.ok && data.cliente) {
        setClienteMudanza(data.cliente);
      } else {
        setClienteMudanza(null);
        lanzarToast(
          "error",
          data.mensaje || "No se encontró el cliente ligado a este serial."
        );
      }
    } catch (e) {
      console.error("Error consultando cliente para mudanza:", e);
      lanzarToast("error", "Error de conexión consultando cliente.");
    } finally {
      setLoadingClienteMudanza(false);
    }
  }

  function cerrarMudanza() {
    setModalMudanza(false);
    setOnuMudanza(null);
    setLoadingMudanza(false);

    // limpiar estado cliente + domicilio nuevo
    setClienteMudanza(null);
    setLoadingClienteMudanza(false);
    setNuevoDomicilio("");
  }

  // 👉 MUDANZA REAL: llama al backend /olt/mudanza_onu
  async function confirmarMudanza() {
    if (!onuMudanza) return;

    const dom = (nuevoDomicilio || "").trim();
    if (!dom) {
      lanzarToast("error", "Escribe el nuevo domicilio antes de continuar.");
      return;
    }

    try {
      setLoadingMudanza(true);

      const res = await authFetch("/api/olt/mudanza_onu", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          olt_id: oltId,
          serial: onuMudanza.sn, // SN de la ONU ya registrada
          new_port: onuMudanza.puerto, // F/S/P detectado en autofind
          new_direccion: dom, // ✅ nuevo
        }),
      });

      const data = await res.json();

      if (!data.ok) {
        lanzarToast("error", data.mensaje || "Error al reubicar ONU.");
        setLoadingMudanza(false);
        return;
      }

      lanzarToast("success", "Mudanza realizada correctamente.");
      const snMudada = onuMudanza?.sn;
      cerrarMudanza();
      // ✅ Sin re-consultar la OLT: la quitamos de la lista local
      if (snMudada) quitarDeLista(snMudada);
    } catch (e) {
      console.error("Error en mudanza ONU:", e);
      lanzarToast("error", "Error de conexión con el servidor.");
    } finally {
      setLoadingMudanza(false);
    }
  }

  // ============================================================
  // ELIMINAR ONU (ya registrada)
  // ============================================================
  function abrirEliminar(onu) {
    setOnuEliminar(onu);
    setModalDelete(true);
  }

  function cancelarEliminar() {
    setModalDelete(false);
    setOnuEliminar(null);
    setLoadingDelete(false);
  }

  async function confirmarEliminar() {
    if (!onuEliminar) return;

    try {
      setLoadingDelete(true);

      const res = await authFetch("/api/olt/eliminar_onu", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          olt_id: oltId,
          serial: onuEliminar.sn,
        }),
      });

      const data = await res.json();

      if (!data.ok) {
        lanzarToast("error", data.mensaje || "Error al eliminar ONU.");
        setLoadingDelete(false);
        return;
      }

      lanzarToast("success", "ONU eliminada correctamente.");
      const snEliminada = onuEliminar?.sn;
      cancelarEliminar();
      // ✅ Sin re-consultar la OLT: la quitamos de la lista local
      if (snEliminada) quitarDeLista(snEliminada);
    } catch (e) {
      console.error("Error eliminando ONU:", e);
      lanzarToast("error", "Error de conexión con el servidor.");
    } finally {
      setLoadingDelete(false);
    }
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="page-container">
      <EncabezadoPagina
        icono={Iconos.antena}
        titulo="ONUs No Autorizadas"
        resumen={`${onus.length} detectada${onus.length === 1 ? "" : "s"} en la OLT`}
        acciones={
          <>
            {/* Con un solo nodo no hay nada que elegir; con dos, elegir
                es obligatorio: autorizar en el equivocado no se deshace
                con un botón. */}
            {olts.length > 1 && (
              <select
                className="onus-selector-olt"
                value={oltId ?? ""}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setOltId(id);
                  setOnus([]);
                  cargarAutofind(id);
                }}
              >
                {olts.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nombre || o.host}
                  </option>
                ))}
              </select>
            )}
            <Boton
              variante="secundario"
              icono={Iconos.actualizar}
              cargando={loadingAutofind}
              disabled={!oltId}
              onClick={() => cargarAutofind()}
            >
              Actualizar
            </Boton>
          </>
        }
      />

      <Tarjeta sinEspacio>
        {loadingAutofind ? (
          <div className="loading-box">
            <div className="spinner"></div>
            <p>Cargando ONUs desde la OLT…</p>
          </div>
        ) : (
          <table className="onus-table">
            <thead>
              <tr>
                <th>F/S/P</th>
                <th>Modelo</th>
                <th>Serial</th>
                <th>MAC</th>
                <th>Detectada</th>
                <th>Estado local</th>
                <th>Acción</th>
              </tr>
            </thead>

            <tbody>
              {onus.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty">
                    No hay ONUs no autorizadas
                  </td>
                </tr>
              ) : (
                onus.map((o, i) => {
                  const yaReg = Boolean(o.ya_registrada);

                  return (
                    <tr key={i}>
                      <td>{o.puerto}</td>
                      <td>{o.model}</td>
                      <td>{o.sn}</td>
                      <td>{o.mac}</td>
                      <td>{o.autofind_time}</td>

                      <td>
                        {/* "Reubicar" = el serial ya existe en la base: es
                            un equipo que cambió de puerto, no un alta. */}
                        {yaReg ? (
                          <Insignia variante="suspendido" icono={Iconos.ubicacion} texto="Reubicar" />
                        ) : (
                          <Insignia variante="info" icono={Iconos.antena} texto="Nueva ONU" />
                        )}
                      </td>

                      <td>
                        <div className="acciones-onu">
                          {!yaReg && (
                            <Boton
                              icono={Iconos.confirmar}
                              onClick={() => abrirAutorizar(o)}
                            >
                              Autorizar
                            </Boton>
                          )}

                          {yaReg && (
                            <>
                              <Boton
                                variante="secundario"
                                icono={Iconos.ubicacion}
                                onClick={() => abrirMudanza(o)}
                              >
                                Cambio de domicilio
                              </Boton>

                              <Boton
                                variante="peligro-suave"
                                icono={Iconos.eliminar}
                                onClick={() => abrirEliminar(o)}
                              >
                                Borrar
                              </Boton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </Tarjeta>

      {/* MODAL AUTORIZAR */}
      {modalAutorizar && onuSeleccionada && (
        <ModalAutorizarOnu
          oltId={oltId}
          onu={onuSeleccionada}
          onClose={cerrarAutorizar}
          onSuccess={() => {
            lanzarToast("success", "ONU autorizada correctamente ✔");
            const snAutorizada = onuSeleccionada?.sn;
            cerrarAutorizar();
            // ✅ Sin re-consultar la OLT: la ONU autorizada sale de la lista.
            //    Para una lectura fresca está el botón "Actualizar".
            if (snAutorizada) quitarDeLista(snAutorizada);
          }}
        />

      )}

      {/* MODAL MUDANZA (Vision Pro) */}
      {modalMudanza && onuMudanza && (
        <div className="modal-overlay">
          <div className="vp-card">
            <h3 className="vp-title">Cambio de domicilio de ONU</h3>
            <p className="vp-subtitle">
              Esta ONU ya existe en el sistema. ¿Deseas reubicarla?
            </p>

            <div className="vp-section">
              <div className="vp-section-title">ONU detectada</div>

              <div className="vp-grid">
                <div className="vp-item">
                  <div className="vp-k">Serial</div>
                  <div className="vp-v vp-mono">{onuMudanza.sn}</div>
                </div>

                <div className="vp-item">
                  <div className="vp-k">Puerto detectado</div>
                  <div className="vp-v vp-mono">{onuMudanza.puerto}</div>
                </div>
              </div>
            </div>

            <div className="vp-section">
              <div className="vp-section-title">Cliente asociado</div>

              <div className="vp-client-line">
                {loadingClienteMudanza ? (
                  <span className="vp-muted">Cargando cliente…</span>
                ) : clienteMudanza ? (
                  <>
                    <span className="vp-client-name">
                      {clienteMudanza.nombre} {clienteMudanza.apellidos}
                    </span>
                    <span className="vp-client-id">id: {clienteMudanza.id}</span>
                  </>
                ) : (
                  <span className="vp-muted">
                    No se encontró cliente para este serial.
                  </span>
                )}
              </div>

              {clienteMudanza && (
                <>
                  <div className="vp-grid vp-grid-2">
                    <div className="vp-item">
                      <div className="vp-k">Dirección</div>
                      <div className="vp-v">{clienteMudanza.direccion}</div>
                    </div>

                    <div className="vp-item">
                      <div className="vp-k">Colonia</div>
                      <div className="vp-v">{clienteMudanza.colonia || "N/D"}</div>
                    </div>

                    <div className="vp-item">
                      <div className="vp-k">Teléfono</div>
                      <div className="vp-v vp-mono">{clienteMudanza.telefono || "N/D"}</div>
                    </div>

                    <div className="vp-item">
                      <div className="vp-k">Plan</div>
                      <div className="vp-v">
                        <span className="vp-chip">
                          {clienteMudanza.plan} (${clienteMudanza.costo})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ✅ Nuevo domicilio (fuera del grid) */}
                  <div className="vp-section" style={{ marginTop: 12 }}>
                    <div className="vp-section-title">Nuevo domicilio</div>

                    <textarea
                      value={nuevoDomicilio}
                      onChange={(e) => setNuevoDomicilio(e.target.value)}
                      placeholder="Escribe el nuevo domicilio completo..."
                      className="vp-input"
                      rows={3}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="vp-buttons">
              <Boton variante="secundario" icono={Iconos.cerrar} onClick={cerrarMudanza}>
                Cancelar
              </Boton>
              <Boton
                icono={Iconos.adelante}
                cargando={loadingMudanza}
                onClick={confirmarMudanza}
                disabled={
                  loadingClienteMudanza ||
                  !clienteMudanza ||
                  !nuevoDomicilio.trim()
                }
              >
                Continuar
              </Boton>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR (iOS) */}
      {modalDelete && onuEliminar && (
        <div className="modal-overlay">
          <div className="modal-card-ios">
            <h3>Eliminar ONU</h3>
            <p>
              Esta acción eliminará la ONU de la OLT y del sistema. Esta
              operación no se puede deshacer.
            </p>
            <p className="modal-ios-sn">
              SN: <strong>{onuEliminar.sn}</strong>
            </p>

            <div className="modal-buttons-ios">
              <Boton variante="secundario" icono={Iconos.cerrar} onClick={cancelarEliminar}>
                Cancelar
              </Boton>
              <Boton
                variante="peligro"
                icono={Iconos.eliminar}
                cargando={loadingDelete}
                onClick={confirmarEliminar}
              >
                Eliminar
              </Boton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

